import React, { ChangeEvent, useState, SyntheticEvent } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Typography } from '@material-ui/core';
import ChannelDetails from './ChannelDetails';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { getChannel } from './apiQueries';
import { createOrUpdateChannel } from './apiMutations';
import EditorForm from '../common/EditorForm';
import ChannelDetailsEditor, { CheckBoxChangeEvent, SelectChangeEvent } from './ChannelDetailsEditor';
import { Channel as ChannelType, VideoAssetConnection } from '../../utils/types';
import { convertChannelToChannelInput } from '../../utils/inputTransform';
import { useAlertContext } from '../../context/alertContext';

export const Channel: React.FC<ChannelProps> = (props) => {
	const { id }: ChannelParams = useParams();
	const { edit } = props;
	const { setAlert } = useAlertContext();
	const history = useHistory();
	const [updatedName, setUpdatedName] = useState<string | null>(null);
	const [updatedLanguage, setUpdatedLanguage] = useState<string>('');
	const [updatedTerritory, setUpdatedTerritory] = useState<string>('');
	const [updatedType, setUpdatedType] = useState<string>('');
	const [updatedHighlights, setUpdatedHighlights] = useState<boolean | null>(null);
	const [updatedSourceAssets, setUpdatedSourceAssets] = useState<VideoAssetConnection>({ items: [] });
	const [updatedMediaLiveId, setUpdatedMediaLiveId] = useState<string | null>(null);
	const [updatedMediaPackageId, setUpdatedMediaPackageId] = useState<string | null>(null);
	const [saveDisabled, setSaveDisabled] = useState(true);

	const [updateChannel] = useMutation(createOrUpdateChannel);

	const { loading, error, data, refetch } = useQuery(getChannel, {
		variables: { id: decodeURIComponent(id) }
	});

	if (loading) return <Typography>Loading...</Typography>;
	if (error) return <Typography>Error loading data from the server</Typography>;
	if (!data.getChannel) return <Typography>No channel found</Typography>;

	const handleChange = (event: ChangeEvent<HTMLInputElement> | SelectChangeEvent | CheckBoxChangeEvent): void => {
		const { name, value } = event.target;

		switch (name) {
			case 'name':
				setUpdatedName(value as string);
				break;
			case 'language':
				setUpdatedLanguage(value as string);
				break;
			case 'territory':
				setUpdatedTerritory(value as string);
				break;
			case 'type':
				setUpdatedType(value as string);
				break;
			case 'highlights':
				setUpdatedHighlights(value as unknown as boolean);
				break;
			case 'mediaLiveId':
				setUpdatedMediaLiveId(value as string);
				break;
			case 'mediaPackageId':
				setUpdatedMediaPackageId(value as string);
				break;
		}
		setSaveDisabled(false);
	};

	const handleSourceAssetsChange = (assets: VideoAssetConnection): void => {
		setUpdatedSourceAssets(assets);
		setSaveDisabled(false);
	};

	const handleChannelFormCancel = (event: SyntheticEvent): void => {
		history.push(`/channels/${id}`);
	};

	const handleChannelFormSubmit = async (event: SyntheticEvent): Promise<unknown> => {
		event.stopPropagation();
		event.preventDefault();

		const channel: ChannelType = {
			id,
			...(updatedName !== null ? { name: updatedName } : { name }),
			...(updatedLanguage ? { language: updatedLanguage } : { language }),
			...(updatedTerritory ? { territory: updatedTerritory } : { territory }),
			...(updatedType ? { type: updatedType } : { type }),
			...(updatedHighlights !== null ? { highlights: updatedHighlights } : { highlights }),
			...(updatedSourceAssets?.items?.length && updatedSourceAssets?.items?.length > 0 ? { sourceAssets: updatedSourceAssets } : { sourceAssets }),
			...(updatedMediaLiveId !== null ? { mediaLiveId: updatedMediaLiveId } : { mediaLiveId }),
			...(updatedMediaPackageId !== null ? { mediaPackageId: updatedMediaPackageId } : { mediaPackageId })
		};

		const channelInput = convertChannelToChannelInput(channel);

		if (!channel.name || !channel.language || !channel.territory || !channel.type || channel.sourceAssets!.items!.length < 1) {
			setAlert('Some required fields are missing. Required fields: name, language, territory, type, source assets');
		} else if (window.confirm('Are you sure you want to update the channel? This cannot be undone.')) {
			setSaveDisabled(true);
			try {
				await updateChannel({ variables: { channel: channelInput } });
				await refetch();
				setAlert('Channel updated');
				history.push(`/channels/${id}`);
			} catch {
				setAlert('Error updating channel');
				setSaveDisabled(false);
			}
		}
		return true;
	};

	const { name, language, territory, type, highlights, sourceAssets, mediaLiveId, mediaPackageId } = data.getChannel;

	return edit ? (
		<EditorForm
			cancelText='Cancel'
			onCancel={handleChannelFormCancel}
			onSubmit={handleChannelFormSubmit}
			submitText='Update Channel'
			saveDisabled={saveDisabled}>
			<ChannelDetailsEditor
				id={id}
				name={updatedName !== null ? updatedName : name}
				language={updatedLanguage || language}
				territory={updatedTerritory || territory}
				type={updatedType || type}
				highlights={updatedHighlights !== null ? updatedHighlights : highlights}
				sourceAssets={updatedSourceAssets?.items?.length && updatedSourceAssets?.items?.length > 0 ? updatedSourceAssets : sourceAssets}
				mediaLiveId={updatedMediaLiveId !== null ? updatedMediaLiveId : mediaLiveId}
				mediaPackageId={updatedMediaPackageId !== null ? updatedMediaPackageId : mediaPackageId}
				onChange={handleChange}
				onSourceAssetsChange={handleSourceAssetsChange}
			/>
		</EditorForm>
	) : (
		<ChannelDetails
			id={id}
			name={name}
			language={language}
			territory={territory}
			type={type}
			highlights={highlights}
			sourceAssets={sourceAssets}
			mediaLiveId={mediaLiveId}
			mediaPackageId={mediaPackageId}
		/>
	);
};

type ChannelParams = { id: string };

type ChannelProps = { edit?: boolean };

export default Channel;
